import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Button,
  Typography,
  CardContent,
  Container,
  Alert,
  CircularProgress,
  Paper,
  Divider,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Person as PersonIcon,
  School as SchoolIcon,
  ArrowBack as ArrowBackIcon,
  Search as SearchIcon,
  Gavel as GavelIcon,
} from '@mui/icons-material';
import { userService } from '../services/userService';
import { householdService } from '../services/householdService';
import { roleService } from '../services/roleService';
import { locationService } from '../services/locationService';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSentryTracking } from './SentryComponentWrapper';
import { 
  trackFormSubmission, 
  trackUIInteraction, 
  trackAPICall,
  addBreadcrumb, 
  logger, 
  setContext,
  setTag,
  trackError 
} from '../utils/sentryUtils';

const StudentSignUpForm = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Initialize Sentry tracking for this component
  useSentryTracking('StudentSignUpForm', { includeUpdates: true, includeRender: true });
  
  const [formData, setFormData] = useState({
    firstName: '',
    preferredName: '',
    middleName: '',
    noMiddleName: false,
    lastName: '',
    email: '',
    phoneNumber: '',
    primaryAddressStreet: '',
    primaryAddressCity: '',
    primaryAddressZip: '',
    gender: '',
    district: '',
    school: '',
    graduationYear: '',
    tshirtSize: '',
    seekingTeam: false,
    optInCommunications: false,
    isActive: true,
    roleIds: [],
    // Household selection
    householdCode: '',
    // Privacy policy consent
    privacyPolicyConsent: false,
    // Student age/guardian confirmation
    studentAgeGuardianConsent: false,
    // Avondale School District afterschool activities eligibility
    avondaleEligibilityConsent: false,
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedHousehold, setSelectedHousehold] = useState(null);
  const [searchingHousehold, setSearchingHousehold] = useState(false);
  const [locations, setLocations] = useState([]);
  const [avondaleLocations, setAvondaleLocations] = useState([]);

  // Load locations for school selection
  useEffect(() => {
    const loadLocations = async () => {
      try {
        const allLocations = await locationService.getAllActiveLocations();
        setLocations(allLocations);
        
        // Filter Avondale locations (excluding GATE which is a hub)
        const avondale = allLocations.filter(loc => 
          loc.isActive
        );
        setAvondaleLocations(avondale);
      } catch (err) {
        trackError(err, {
          component: "StudentSignUpForm",
          action: "load_locations"
        });
        logger.error("Error loading locations", {
          component: "StudentSignUpForm",
          error: err.message
        });
      }
    };
    
    loadLocations();
  }, []);

  // Handle URL parameters for pre-filling household code
  useEffect(() => {
    const householdCode = searchParams.get('householdCode');
    
    // Set Sentry context for form initialization
    setContext("signup_form", {
      formType: "student",
      householdCode: householdCode,
      urlParams: Object.fromEntries(searchParams.entries())
    });
    
    setTag("signup.form_type", "student");
    setTag("signup.has_household_code", !!householdCode);
    
    addBreadcrumb("Student signup form opened", "ui.form", {
      formType: "student",
      householdCode: householdCode
    });
    
    logger.info("Student signup form initialized", {
      formType: "student",
      householdCode: householdCode,
      urlParams: Object.fromEntries(searchParams.entries())
    });
    
    if (householdCode) {
      setFormData(prev => ({
        ...prev,
        householdCode: householdCode.toUpperCase()
      }));
      
      // Automatically look up the household
      handleHouseholdCodeLookup(householdCode);
    }
  }, [searchParams]);

  // Phone number formatting function
  const formatPhoneNumber = (value) => {
    // Remove all non-digits
    const phoneNumber = value.replace(/\D/g, '');
    
    // Format based on length
    if (phoneNumber.length === 0) return '';
    if (phoneNumber.length <= 3) return `(${phoneNumber}`;
    if (phoneNumber.length <= 6) return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
  };

  const handlePhoneNumberChange = (event) => {
    const value = event.target.value;
    const formattedValue = formatPhoneNumber(value);
    
    setFormData(prev => ({
      ...prev,
      phoneNumber: formattedValue
    }));
  };

  // Zip code validation function
  const handleZipCodeChange = (event) => {
    const value = event.target.value;
    // Only allow numbers
    const numericValue = value.replace(/\D/g, '');
    
    setFormData(prev => ({
      ...prev,
      primaryAddressZip: numericValue
    }));
  };

  const handleChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleHouseholdCodeLookup = async (code) => {
    if (!code || code.trim().length !== 4) {
      setSelectedHousehold(null);
      return;
    }

    return trackAPICall(
      "GET",
      `/api/households/code/${code.trim().toUpperCase()}`,
      async () => {
        setSearchingHousehold(true);
        
        addBreadcrumb("Looking up household code", "api", {
          code: code.trim().toUpperCase(),
          formType: "student"
        });
        
        try {
          const household = await householdService.getHouseholdByCode(code.trim().toUpperCase());
          setSelectedHousehold(household);
          
          if (errors.householdCode) {
            setErrors(prev => ({
              ...prev,
              householdCode: ''
            }));
          }
          
          logger.info("Household code lookup successful", {
            code: code.trim().toUpperCase(),
            householdId: household?.id,
            formType: "student"
          });
          
          return household;
        } catch (err) {
          setSelectedHousehold(null);
          setErrors(prev => ({
            ...prev,
            householdCode: 'Invalid household code. Please check and try again.'
          }));
          
          trackError(err, {
            component: "StudentSignUpForm",
            action: "household_code_lookup",
            code: code.trim().toUpperCase()
          });
          
          logger.error("Household code lookup failed", {
            code: code.trim().toUpperCase(),
            error: err.message,
            formType: "student"
          });
          
          throw err;
        } finally {
          setSearchingHousehold(false);
        }
      },
      {
        code: code.trim().toUpperCase(),
        formType: "student"
      }
    );
  };

  const handleDistrictChange = (district) => {
    setFormData(prev => ({
      ...prev,
      district,
      school: '' // Reset school when district changes
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    // Middle name validation: either middle name must be filled OR "I don't have a middle name" must be checked
    if (!formData.middleName.trim() && !formData.noMiddleName) {
      newErrors.middleName = 'Please enter your middle name or check "I don\'t have a middle name"';
    }

    if (!formData.gender) {
      newErrors.gender = 'Gender identity is required';
    }
    if (!formData.district.trim()) {
      newErrors.district = 'School district is required';
    }
    if (!formData.school.trim()) {
      newErrors.school = 'School name is required';
    }
    if (!formData.graduationYear) {
      newErrors.graduationYear = 'Graduation year is required';
    }
    if (!formData.tshirtSize) {
      newErrors.tshirtSize = 'T-shirt size is required';
    }
    if (!formData.householdCode.trim()) {
      newErrors.householdCode = 'Household code is required';
    } else if (formData.householdCode.trim().length !== 4) {
      newErrors.householdCode = 'Household code must be 4 characters';
    } else if (!selectedHousehold) {
      newErrors.householdCode = 'Please enter a valid household code';
    }
    
    // Privacy policy consent is required
    if (!formData.privacyPolicyConsent) {
      newErrors.privacyPolicyConsent = 'You must read and accept the Privacy Policy to continue';
    }
    
    // Student age/guardian confirmation is required
    if (!formData.studentAgeGuardianConsent) {
      newErrors.studentAgeGuardianConsent = 'You must confirm that you are a student 13 years of age or older OR you are the parent/legal guardian of the student registering';
    }
    
    // Avondale School District afterschool activities eligibility is required
    if (!formData.avondaleEligibilityConsent) {
      newErrors.avondaleEligibilityConsent = 'You must affirm that your student is eligible to be involved in Avondale School District afterschool activities';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Track form submission with Sentry
    return trackFormSubmission(
      "StudentSignUpForm",
      async () => {
        if (!validateForm()) {
          throw new Error("Form validation failed");
        }
        
        setLoading(true);
        setError('');
        
        addBreadcrumb("Student signup form submitted", "ui.form", {
          formType: "student",
          hasHouseholdCode: !!formData.householdCode,
          householdId: selectedHousehold?.id
        });
        
        logger.info("Student signup form submitted", {
          formType: "student",
          hasHouseholdCode: !!formData.householdCode,
          householdId: selectedHousehold?.id,
          formFields: Object.keys(formData)
        });
        
        try {
        // Get the student role
        const studentRole = await roleService.getRoleByName('Student');
        
        if (!studentRole) {
          throw new Error('Student role not found in system');
        }
        
        // Create the user with student role and selected household
        const userData = {
          ...formData,
          // Filter out empty enum values
          gender: formData.gender || null,
          tshirtSize: formData.tshirtSize || null,
          roleIds: [studentRole.id],
          householdIds: [selectedHousehold.id],
        };
        
        const createdUser = await userService.signupUser(userData);
        
        // Check if there's already an authenticated caregiver session
        const existingAuthenticated = sessionStorage.getItem('householdAuthenticated') === 'true';
        const existingUserId = sessionStorage.getItem('userId');
        
        if (existingAuthenticated && existingUserId) {
          // If there's already a session, redirect to household dashboard with league enrollment flag
          // and pass the newly created student's ID
          navigate(`/household/dashboard?openLeagueEnrollment=true&studentId=${createdUser.id}`);
        } else {
          // No existing session, redirect to login page after successful signup
          navigate('/login');
        }
        setFormData({
          firstName: '',
          preferredName: '',
          middleName: '',
          noMiddleName: false,
          lastName: '',
          email: '',
          phoneNumber: '',
          primaryAddressStreet: '',
          primaryAddressCity: '',
          primaryAddressZip: '',
          additionalAddressStreet: '',
          additionalAddressCity: '',
          additionalAddressZip: '',
          gender: '',
          district: '',
          school: '',
          graduationYear: '',
          tshirtSize: '',
                  seekingTeam: false,
        optInCommunications: false,
          isActive: true,
          roleIds: [],
          householdCode: '',
          privacyPolicyConsent: false,
          studentAgeGuardianConsent: false,
          avondaleEligibilityConsent: false,
        });
      } catch (err) {
        // Track error with Sentry
        trackError(err, {
          component: "StudentSignUpForm",
          action: "form_submission",
          hasHouseholdCode: !!formData.householdCode,
          householdId: selectedHousehold?.id
        });
        
        logger.error("Student signup failed", {
          component: "StudentSignUpForm",
          error: err.message,
          hasHouseholdCode: !!formData.householdCode,
          householdId: selectedHousehold?.id
        });
        
        setError('Failed to register. Please try again.');
      } finally {
        setLoading(false);
      }
    },
    {
      formFields: Object.keys(formData),
      hasHouseholdCode: !!formData.householdCode,
      householdId: selectedHousehold?.id,
      hasValidationErrors: Object.keys(errors).length > 0
    }
  )};

  const tshirtSizes = ['Youth Small', 'Youth Medium', 'Youth Large', 'Adult Small', 'Adult Medium', 'Adult Large', 'Adult XL', 'Adult XXL'];
  const currentYear = new Date().getFullYear();
  const graduationYears = Array.from({ length: 14 }, (_, i) => (currentYear + i).toString());



  return (
    <Container maxWidth="lg" sx={{ mt: 2, mb: 4 }}>
      {/* Header Section */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Button
          variant="text"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/')}
          sx={{ mb: 2 }}
        >
          Back to Registration Types
        </Button>
        <Typography 
          variant={isMobile ? "h4" : "h3"} 
          component="h1" 
          gutterBottom 
          color="primary"
          sx={{ 
            fontWeight: 'bold',
            mb: 1
          }}
        >
          Student Registration
        </Typography>
        <Typography 
          variant="h6" 
          color="text.secondary" 
          sx={{ 
            mb: 3,
            maxWidth: '600px',
            mx: 'auto'
          }}
        >
          Register a student and join your family's household
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Main Form */}
      <Paper elevation={2} sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <Box component="form" onSubmit={handleSubmit}>
          
          {/* Personal Information Section */}
          <Box sx={{ p: { xs: 2, md: 4 }, backgroundColor: 'primary.main', color: 'white' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <PersonIcon sx={{ mr: 1.5, fontSize: 28 }} />
              <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                Student Information
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Tell us about yourself / your student
            </Typography>
          </Box>
          
          <CardContent sx={{ p: { xs: 2, md: 4 } }}>
            <Grid container spacing={3}>
              {/* Name Fields */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="First Name (Legal)"
                  value={formData.firstName}
                  onChange={handleChange('firstName')}
                  error={!!errors.firstName}
                  helperText={errors.firstName}
                  required
                  size="medium"
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Last Name (Legal)"
                  value={formData.lastName}
                  onChange={handleChange('lastName')}
                  error={!!errors.lastName}
                  helperText={errors.lastName}
                  required
                  size="medium"
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Preferred Name"
                  value={formData.preferredName}
                  onChange={handleChange('preferredName')}
                  helperText="Name you / your student go by (e.g., Matt for Matthew)"
                  size="medium"
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <TextField
                    fullWidth
                    label="Middle Name"
                    value={formData.middleName}
                    onChange={handleChange('middleName')}
                    disabled={formData.noMiddleName}
                    error={!!errors.middleName}
                    helperText={errors.middleName}
                    size="medium"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.noMiddleName}
                        onChange={handleChange('noMiddleName')}
                        size="small"
                      />
                    }
                    label="I don't have a middle name"
                    sx={{ ml: 0 }}
                  />
                </Box>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Phone Number (Optional)"
                  type="tel"
                  value={formData.phoneNumber}
                  onChange={handlePhoneNumberChange}
                  helperText="e.g., (555) 123-4567"
                  size="medium"
                  inputProps={{
                    maxLength: 14, // (XXX) XXX-XXXX format
                    placeholder: "(555) 123-4567"
                  }}
                />
              </Grid>
              
              <Grid item xs={12} md={4}>
                <FormControl fullWidth size="medium" sx={{ minWidth: 160 }}>
                  <InputLabel>Gender Identity *</InputLabel>
                  <Select
                    value={formData.gender}
                    onChange={handleChange('gender')}
                    label="Gender Identity *"
                    error={!!errors.gender}
                  >
                    <MenuItem value="MALE">Male</MenuItem>
                    <MenuItem value="FEMALE">Female</MenuItem>
                    <MenuItem value="NON_BINARY">Non-Binary</MenuItem>
                    <MenuItem value="NO_ANSWER">Prefer not to answer</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <FormControl fullWidth size="medium" sx={{ minWidth: 120 }}>
                  <InputLabel>T-shirt Size *</InputLabel>
                  <Select
                    value={formData.tshirtSize}
                    onChange={handleChange('tshirtSize')}
                    label="T-shirt Size *"
                    error={!!errors.tshirtSize}
                  >
                    {tshirtSizes.map((size) => (
                      <MenuItem key={size} value={size}>
                        {size}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </CardContent>

          <Divider />

          {/* Household Selection Section */}
          <Box sx={{ p: { xs: 2, md: 4 }, backgroundColor: 'grey.50' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <SearchIcon sx={{ mr: 1.5, fontSize: 28, color: 'primary.main' }} />
              <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                Household Selection
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              Enter your family's household code
            </Typography>
          </Box>
          
          <CardContent sx={{ p: { xs: 2, md: 4 } }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Household Code"
                  value={formData.householdCode}
                  onChange={(e) => {
                    const value = e.target.value.toUpperCase();
                    setFormData(prev => ({
                      ...prev,
                      householdCode: value
                    }));
                    if (value.length === 4) {
                      handleHouseholdCodeLookup(value);
                    } else {
                      setSelectedHousehold(null);
                    }
                    if (errors.householdCode) {
                      setErrors(prev => ({
                        ...prev,
                        householdCode: ''
                      }));
                    }
                  }}
                  error={!!errors.householdCode}
                  helperText={errors.householdCode || "Enter the 4-character household code provided by your family"}
                  required
                  size="medium"
                  inputProps={{
                    maxLength: 4,
                    style: { textTransform: 'uppercase' }
                  }}
                  InputProps={{
                    endAdornment: searchingHousehold ? <CircularProgress size={20} /> : null,
                  }}
                />
              </Grid>
              
              {selectedHousehold && (
                <Grid item xs={12} md={6}>
                  <Box sx={{ 
                    p: 2, 
                    border: 1, 
                    borderColor: 'success.main', 
                    borderRadius: 1,
                    backgroundColor: 'success.light',
                    color: 'success.contrastText'
                  }}>
                    <Typography variant="body2" fontWeight="bold" gutterBottom>
                      Household Found:
                    </Typography>
                    <Typography variant="body2">
                      {selectedHousehold.name}
                    </Typography>

                  </Box>
                </Grid>
              )}
            </Grid>
          </CardContent>

          <Divider />

          {/* School Information Section */}
          <Box sx={{ p: { xs: 2, md: 4 }, backgroundColor: 'primary.main', color: 'white' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <SchoolIcon sx={{ mr: 1.5, fontSize: 28 }} />
              <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                School Information
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Your/Your child's academic details
            </Typography>
          </Box>
          
          <CardContent sx={{ p: { xs: 2, md: 4 } }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth size="medium" require sx={{ minWidth: 160 }}>
                  <InputLabel>School District *</InputLabel>
                  <Select
                    value={formData.district}
                    onChange={(e) => handleDistrictChange(e.target.value)}
                    label="School District *"
                    error={!!errors.district}
                  >
                    <MenuItem value="Avondale">Avondale</MenuItem>
                    <MenuItem value="Other">Other</MenuItem>
                  </Select>
                </FormControl>
                {errors.district && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                    {errors.district}
                  </Typography>
                )}
              </Grid>
              
              <Grid item xs={12} md={4}>
                {formData.district === 'Avondale' ? (
                  <FormControl fullWidth size="medium" required sx={{ minWidth: 160 }}>
                    <InputLabel>School Name</InputLabel>
                    <Select
                      value={formData.school}
                      onChange={handleChange('school')}
                      label="School Name *"
                      error={!!errors.school}
                      disabled={!formData.district}
                    >
                      {avondaleLocations.map((location) => (
                        <MenuItem key={location.id} value={location.name}>
                          {location.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : (
                  <TextField
                    fullWidth
                    label="School Name"
                    value={formData.school}
                    onChange={handleChange('school')}
                    error={!!errors.school}
                    helperText={!formData.district ? "Please select a school district first" : errors.school}
                    required
                    size="medium"
                    disabled={!formData.district}
                  />
                )}
              </Grid>
              
              <Grid item xs={12} md={4}>
                <FormControl fullWidth size="medium" sx={{ minWidth: 170 }}>
                  <InputLabel>Graduation Year *</InputLabel>
                  <Select
                    value={formData.graduationYear}
                    onChange={handleChange('graduationYear')}
                    label="Graduation Year *"
                    error={!!errors.graduationYear}
                  >
                    {graduationYears.map((year) => (
                      <MenuItem key={year} value={year}>
                        {year}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </CardContent>

          <Divider />

          {/* Privacy Policy Consent Section */}
          <Box sx={{ p: { xs: 2, md: 4 }, backgroundColor: 'primary.main', color: 'white' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <GavelIcon sx={{ mr: 1.5, fontSize: 28 }} />
              <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                Legal Consent
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Please review and accept our privacy policy to continue with registration
            </Typography>
          </Box>
          
          <CardContent sx={{ p: { xs: 2, md: 4 } }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.privacyPolicyConsent}
                    onChange={handleChange('privacyPolicyConsent')}
                    color="primary"
                  />
                }
                label={
                  <Typography variant="body1" sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                    I have read and understand the{' '}
                    <Button
                      variant="text"
                      size="small"
                      onClick={() => window.open('/privacy-policy', '_blank')}
                      sx={{ 
                        p: 0, 
                        minWidth: 'auto',
                        textDecoration: 'underline',
                        color: 'primary.main',
                        '&:hover': {
                          textDecoration: 'none'
                        }
                      }}
                    >
                      RoboJackets K12 Foundation's Privacy Policy
                    </Button>
                    , including how my (or my child's) information will be used to operate and promote the FTC League and sister programs.*
                  </Typography>
                }
                sx={{ 
                  alignItems: 'flex-start',
                  '& .MuiFormControlLabel-label': {
                    mt: 0.5
                  }
                }}
              />
            </Box>
            {errors.privacyPolicyConsent && (
              <Typography variant="body2" color="error" sx={{ mt: 1, ml: 4 }}>
                {errors.privacyPolicyConsent}
              </Typography>
            )}
            
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mt: 3 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.studentAgeGuardianConsent}
                    onChange={handleChange('studentAgeGuardianConsent')}
                    color="primary"
                  />
                }
                label={
                  <Typography variant="body1" sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                    I confirm that I am a student 13 years of age or older OR I am the parent/legal guardian of the student registering.*
                  </Typography>
                }
                sx={{ 
                  alignItems: 'flex-start',
                  '& .MuiFormControlLabel-label': {
                    mt: 0.5
                  }
                }}
              />
            </Box>
            {errors.studentAgeGuardianConsent && (
              <Typography variant="body2" color="error" sx={{ mt: 1, ml: 4 }}>
                {errors.studentAgeGuardianConsent}
              </Typography>
            )}
            
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mt: 3 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.avondaleEligibilityConsent}
                    onChange={handleChange('avondaleEligibilityConsent')}
                    color="primary"
                  />
                }
                label={
                  <Typography variant="body1" sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                    I affirm that my student is eligible to be involved in Avondale School District afterschool activities.*
                  </Typography>
                }
                sx={{ 
                  alignItems: 'flex-start',
                  '& .MuiFormControlLabel-label': {
                    mt: 0.5
                  }
                }}
              />
            </Box>
            {errors.avondaleEligibilityConsent && (
              <Typography variant="body2" color="error" sx={{ mt: 1, ml: 4 }}>
                {errors.avondaleEligibilityConsent}
              </Typography>
            )}
          </CardContent>

          {/* Submit Section */}
          <Box sx={{ 
            p: { xs: 3, md: 4 }, 
            backgroundColor: 'primary.main', 
            color: 'white',
            textAlign: 'center'
          }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 'bold' }}>
              Ready to Join RoboJackets?
            </Typography>
            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={true}
              sx={{ 
                minWidth: 200,
                py: 1.5,
                px: 4,
                backgroundColor: 'white',
                color: 'primary.main',
                opacity: 0.5,
                cursor: 'not-allowed',
                '&:hover': {
                  backgroundColor: 'white',
                }
              }}
            >
              {loading ? <CircularProgress size={24} color="primary" /> : 'Complete Registration'}
            </Button>
            
            {/* Show message explaining why registration is disabled */}
            <Typography variant="body2" sx={{ mt: 2, opacity: 0.8, fontStyle: 'italic' }}>
              Student registration is currently disabled as the season has started. 
              Please contact your team coach or administrator for assistance with student enrollment.
            </Typography>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default StudentSignUpForm; 